// src/documents/attorneyExport.js
//
// Expediente para el abogado (must have, reunión 22-sep; paso "Abogado /
// NOI" del flujo): un ZIP con
//   Cronologia.xlsx          hoja "Cronologia" (notas, pasos, agenda, firmas, closings) + hoja "Reclamo"
//   Documentos/<carpetas>/…  adjuntos del reclamo, con su estructura de carpetas
//   Firmados/…               LOR y SPOL firmadas, Closings firmados
//   LEEME.txt                qué contiene y qué archivos no se pudieron incluir
//
// No incluye las solicitudes internas entre PA y CSR (pregunta pendiente al
// cliente). La descarga de cada archivo se inyecta (fetchFile) para poder
// probarlo sin servidor; si un archivo falla, se anota y se sigue.
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { getStep } from '../services/flowService';
import { insuredNames } from './spolData';

const toDate = v => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const iso = v => {
  const d = toDate(v);
  return d && !Number.isNaN(d.getTime()) ? d.toISOString().slice(0, 16).replace('T', ' ') : '';
};
const stepName = id => getStep('claim', id)?.label.es || id || '—';
const safe = name => String(name || 'archivo').replace(/[\\/:*?"<>|]+/g, '-').trim() || 'archivo';

// Línea de tiempo única, ordenada por fecha: notas, movimientos de paso,
// agenda, documentos firmados y closings.
export const buildTimeline = ({ claimRecord = {}, notes = [], events = [], history = [], closings = [] }) => {
  const rows = [];
  notes.forEach(n => rows.push({ fecha: iso(n.createdAt), tipo: 'Nota', detalle: n.text, autor: n.createdByName || n.createdByEmail || '' }));
  history.forEach(h => rows.push({
    fecha: iso(h.at), tipo: 'Paso del reclamo',
    detalle: `${stepName(h.fromStep)} → ${stepName(h.toStep)}${h.reason ? ` (${h.reason})` : ''}`, autor: h.byEmail || ''
  }));
  events.forEach(e => rows.push({
    fecha: `${e.date || ''}${e.time ? ` ${e.time}` : ''}`, tipo: 'Agenda',
    detalle: `${e.type || ''}${e.confirmed ? ' (confirmado)' : ''}${e.notes ? ` — ${e.notes}` : ''}`, autor: ''
  }));
  Object.entries(claimRecord.signedDocuments || {}).forEach(([docType, d]) => {
    if (d.sentDate) rows.push({ fecha: d.sentDate, tipo: `${docType.toUpperCase()} enviada a firmar`, detalle: d.signatureUrl || d.docusignUrl || '', autor: '' });
    if (d.signedDate) rows.push({ fecha: d.signedDate, tipo: `${docType.toUpperCase()} firmada`, detalle: d.signedPdf?.name || '', autor: '' });
  });
  closings.forEach(c => rows.push({
    fecha: c.date || iso(c.createdAt), tipo: 'Closing Statement',
    detalle: `${c.paymentType || ''} · cheque ${c.checkNumber || '—'} · $${Number(c.checkAmount || 0).toFixed(2)} · neto $${Number(c.netToClient || 0).toFixed(2)} · ${c.status || 'draft'}`,
    autor: c.createdBy || ''
  }));
  return rows.sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)));
};

// Ruta de carpetas "Padre/Hijo" de cada carpeta de adjuntos.
export const folderPaths = folders => {
  const byId = new Map(folders.map(f => [f.id, f]));
  const pathOf = (id, seen = new Set()) => {
    const f = byId.get(id);
    if (!f || seen.has(id)) return '';
    seen.add(id);
    const parent = f.parentFolderId ? pathOf(f.parentFolderId, seen) : '';
    return parent ? `${parent}/${safe(f.name)}` : safe(f.name);
  };
  return new Map(folders.map(f => [f.id, pathOf(f.id)]));
};

const claimSheet = cr => [
  ['Asegurado(s)', insuredNames(cr)], ['Claim #', cr.claimNumber], ['Póliza', cr.policyNumber],
  ['Aseguradora', cr.insuranceCompanyName], ['Date of Loss', cr.incidentDate], ['Causa', cr.cause],
  ['Descripción', cr.lossDescription], ['Dirección', cr.clientAddress], ['PA asignado', cr.adjusterName],
  ['Decisión de cobertura', cr.coverageDecision], ['Desk Adjuster', cr.deskAdjusterName], ['Field Adjuster', cr.insuranceAdjusterName],
  ['Abogado', cr.attorneyName], ['Paso actual', stepName(cr.flow?.stepId)]
].map(([campo, valor]) => ({ campo, valor: valor || '' }));

// outputType: 'blob' en el navegador; los tests piden 'uint8array'.
export const buildAttorneyZip = async ({ claimRecord, notes = [], events = [], history = [], closings = [], folders = [], files = [], fetchFile, outputType = 'blob' }) => {
  const zip = new JSZip();
  const failed = [];
  const used = new Set();
  const unique = path => {
    let candidate = path;
    for (let i = 2; used.has(candidate); i += 1) candidate = path.replace(/(\.[^./]+)?$/, ext => ` (${i})${ext || ''}`);
    used.add(candidate);
    return candidate;
  };
  const add = async (path, storageKey) => {
    try {
      zip.file(unique(path), await fetchFile(storageKey));
    } catch (error) {
      failed.push(`${path} — ${error.message}`);
    }
  };

  const paths = folderPaths(folders);
  for (const f of files) await add(`Documentos/${paths.get(f.folderId) || 'Sin carpeta'}/${safe(f.name)}`, f.storageKey);
  for (const [docType, d] of Object.entries(claimRecord.signedDocuments || {})) {
    if (d.signedPdf?.storageKey) await add(`Firmados/${docType.toUpperCase()} firmada.pdf`, d.signedPdf.storageKey);
  }
  for (const c of closings) {
    if (c.signedPdf?.storageKey) await add(`Firmados/Closing ${safe(c.paymentType || '')} cheque ${safe(c.checkNumber || c.id)}.pdf`, c.signedPdf.storageKey);
  }

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(buildTimeline({ claimRecord, notes, events, history, closings })), 'Cronologia');
  XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(claimSheet(claimRecord)), 'Reclamo');
  zip.file('Cronologia.xlsx', XLSX.write(book, { type: 'array', bookType: 'xlsx' }));

  const included = files.length + Object.values(claimRecord.signedDocuments || {}).filter(d => d.signedPdf?.storageKey).length + closings.filter(c => c.signedPdf?.storageKey).length - failed.length;
  zip.file('LEEME.txt', [
    `Expediente del reclamo ${claimRecord.claimNumber || ''} — ${insuredNames(claimRecord)}`,
    `Generado: ${new Date().toISOString().slice(0, 16).replace('T', ' ')} (UTC)`,
    '',
    'Cronologia.xlsx: línea de tiempo (notas, pasos del reclamo, agenda, firmas y closings) y datos del reclamo.',
    'Documentos/: adjuntos del reclamo con su estructura de carpetas.',
    'Firmados/: LOR, SPOL y Closing Statements firmados.',
    '',
    `Archivos incluidos: ${included}`,
    failed.length ? `No se pudieron incluir (${failed.length}):\n${failed.map(f => `  - ${f}`).join('\n')}` : 'Todos los archivos se incluyeron.'
  ].join('\n'));

  return { blob: await zip.generateAsync({ type: outputType }), included, failed };
};
